import { Component } from "@angular/core";
import { ConfirmationService, MessageService } from "primeng/api";
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogModule,
  DynamicDialogRef,
} from "primeng/dynamicdialog";
import {
  AsReportedSecurityBreakdownService,
  FilingService,
  TradingPlanService,
} from "src/api/services";
import { Form } from "../../../../../api/models/form";
import { TradingPlan } from "../../../../../api/models/trading-plan";
import { Filing } from "../../../../../api/models/filing";
import { DiscardConfirmationComponent } from "../discard-confirmation/discard-confirmation.component";
import { formatDate } from "@angular/common";
import {
  AsReportedSecurity,
  AsReportedSecurityBreakdown,
  AsReportedSecurityBreakdownTags,
  TbdFiling,
} from "src/api/models";
import { AddSecurityTagComponent } from "../add-security-tag/add-security-tag.component";
import { AddTextComponent } from "../add-text/add-text.component";

@Component({
  selector: "app-view-data",
  templateUrl: "./view-data.component.html",
  styleUrl: "./view-data.component.css",
  standalone: false,
})
export class ViewDataComponent {
  first = 0;
  rows = 10;
  selectedPlans: TradingPlan[] = []; // To store selected rows
  readonly form: Form = {};
  readonly filing: Filing = {};
  expandedRows: any = {};
  validatedPlans: any[] = []; // Stores validated plans
  disabledPlans: number[] = []; // Stores IDs of plans with disabled icon
  tradingPlansIdsForDeletion: number[] = [];
  deletedPlansCount = 0;
  // securityBreakdownTags?: string[];
  realIdsToDelete: any[] = [];
  selectedTags!: any[];
  isValidationModeActive: boolean = false;

  constructor(
    private _asReportedSecurityBreakdownService: AsReportedSecurityBreakdownService,
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private _filingService: FilingService,
    private _messageService: MessageService,
    private _confirmationService: ConfirmationService,
    private _tradingPlanService: TradingPlanService,
    public dialogService: DialogService
  ) {
    this.form = this.config.data.form;
    this.filing = this.config.data.filing;
  }
  followUpComment: string = "";
  ngOnInit() {
    this.ref.close = this.interceptDialogClose.bind(this);
  }
  addFollowup() {
    if (this.followUpComment?.trim()) {
      const tbdPayload: TbdFiling = {
        filingId: this.filing.filingId!,
        reason: "Follow-up Added",
        comment: this.followUpComment.trim(),
      };

      this._filingService.tbdFiling(tbdPayload).subscribe({
        next: (response: boolean) => {
          if (response) {
            this._messageService.add({
              severity: "success",
              summary: "Success",
              detail: "Follow-up added successfully.",
            });
          } else {
            this._messageService.add({
              severity: "warn",
              summary: "Not Added",
              detail: "TBD filing was not accepted.",
            });
          }
        },
        error: (err) => {
          console.error("TBD filing failed:", err);
          this._messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to add follow-up.",
          });
        },
        complete: () => {
          this.followUpComment = "";
          this.ref.close({ refresh: true });
        },
      });
    }
  }

  // user can edit in LLMm and processed form
  onEditClick() {
    if (this.selectedPlans.length > 0) {
      this.isValidationModeActive = false;
      this.ref.close(this.selectedPlans);
    }
  }

  next() {
    this.first = this.first + this.rows;
  }

  prev() {
    this.first = this.first - this.rows;
  }

  reset() {
    this.first = 0;
  }

  pageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }

  isLastPage(): boolean {
    return this.filing.tradingPlans
      ? this.first + this.rows >= this.filing.tradingPlans.length
      : true;
  }

  isFirstPage(): boolean {
    return this.filing.tradingPlans ? this.first === 0 : true;
  }

  onRowExpand(event: any) {
    this.expandedRows[event.data.tradingPlanId] = true;
  }

  onRowCollapse(event: any) {
    delete this.expandedRows[event.data.tradingPlanId];
  }

  validateForm() {
    const filing: Filing = {
      filingId: this.form.filingId,
      asReportedIssuerId: this.filing.asReportedIssuerId,
      reason: "Plan Details Updated",
      followUpComment: this.filing.followUpComment,
      tradingPlans: this.validatedPlans.map((plan: any) => {
        // Create default modifiedFields object if it's null/undefined
        const planTradingPlanId = /^plan-[a-z0-9]+$/i.test(plan.tradingPlanId)
          ? undefined
          : plan.tradingPlanId;

        let modifiedFields = plan.modifiedFields;

        // If modifiedFields is null/undefined and tradingPlanId exists, create default object
        if (!modifiedFields) {
          modifiedFields = {
            modifiedAdoptionDate: false,
            modifiedAmendmentDate: false,
            modifiedEffectiveDate: false,
            modifiedExpirationDate: false,
            modifiedNumberOfShares: false,
            modifiedPlanAction: false,
            modifiedTerminationDate: false,
            modifiedTransactionType: false,
          };
          // Only add tradingPlanId if it exists (for existing plans)
          if (planTradingPlanId) {
            modifiedFields.tradingPlanId = planTradingPlanId;
          }
        } else if (modifiedFields && planTradingPlanId) {
          // If modifiedFields exists, ensure tradingPlanId is present
          modifiedFields = {
            ...modifiedFields,
            tradingPlanId: planTradingPlanId,
          };
        }

        return {
          tradingPlanId: planTradingPlanId,
          planAction: plan.planAction || undefined,
          relationshipToIssuer: plan.relationshipToIssuer || undefined,
          adoptionDate: this.formatToDateString(plan.adoptionDate) || undefined,
          effectiveDate:
            this.formatToDateString(plan.effectiveDate) || undefined,
          amendmentDate:
            this.formatToDateString(plan.amendmentDate) || undefined,
          modificationComment: plan.modificationComment || undefined,
          modifiedFields: modifiedFields,
          isEstimatedTerminationDate: plan.isEstimatedTerminationDate || false,
          isEstimatedAdoptionDate: plan.isEstimatedAdoptionDate || false,
          isEstimatedAmendedDate: plan.isEstimatedAmendedDate || false,
          terminationDate:
            this.formatToDateString(plan.terminationDate) || undefined,
          expirationDate:
            this.formatToDateString(plan.expirationDate) || undefined,
          durationDays: plan.durationDays || undefined,
          asReportedPlanText: plan.asReportedPlanText || undefined,
          
          earlyTerminationConditions:
            plan.earlyTerminationConditions || undefined,
          tradingConditions: plan.tradingConditions || undefined,
          asReportedInsider: {
            asReportedInsiderName:
              plan.asReportedInsider?.asReportedInsiderName || undefined,
            asReportedReportingEntityName:
              plan.asReportedInsider?.asReportedReportingEntityName || null,
            asReportedReportingEntityType:
              plan.asReportedInsider?.asReportedReportingEntityType || null,
            asReportedInsiderRoleInEntity:
              plan.asReportedInsider?.asReportedInsiderRoleInEntity || null,
               insiderComment:
              plan.asReportedInsider?.insiderComment || null,
              insiderId:plan.asReportedInsider.insiderId || null
          },
          // asReportedSecurityBreakdowns: plan.asReportedSecurityBreakdowns.map(
          //   (sb: any) => ({
          //     asReportedSecurityBreakdownId:
          //       sb.asReportedSecurityBreakdownId || undefined,
          //     // transactionType: sb.transactionType || undefined,
          //     transactionType:
          //       sb.selectedTransactionType ?? sb.transactionType ?? undefined,

          //     asReportedSecurity: {
          //       asReportedSecurityType:
          //         sb.asReportedSecurity?.asReportedSecurityType || undefined,
          //     },
          //     numberOfShares: sb.numberOfShares || undefined,
          //     notionalValue: sb.notionalValue || undefined,
          //     sharesDescription: sb.sharesDescription || undefined,
          //     securityBreakdownTags: sb.securityBreakdownTags
          //       ? {
          //           ...sb.securityBreakdownTags,
          //           asReportedSecurityBreakdownId:
          //             sb.asReportedSecurityBreakdownId,
          //         }
          //       : undefined,
          //   })
          // ),
          asReportedSecurityBreakdowns: plan.asReportedSecurityBreakdowns.map(
            (sb: any) => {
              const txType =
                sb.selectedTransactionType ?? sb.transactionType ?? undefined;

              let tags = sb.securityBreakdownTags || undefined;

              //  BUY → sellToCover hata do
              if (txType === "BUY" && tags) {
                tags = { ...tags };
                delete (tags as any).sellToCover;
              }

              return {
                asReportedSecurityBreakdownId:
                  sb.asReportedSecurityBreakdownId || undefined,
                transactionType: txType,
                asReportedSecurity: {
                  asReportedSecurityType:
                    sb.asReportedSecurity?.asReportedSecurityType || undefined,
                },
                numberOfShares: sb.numberOfShares || undefined,
                notionalValue: sb.notionalValue || undefined,
                sharesDescription: sb.sharesDescription || undefined,
                securityBreakdownTags: tags
                  ? {
                      ...tags,
                      asReportedSecurityBreakdownId:
                        sb.asReportedSecurityBreakdownId,
                    }
                  : undefined,
              };
            }
          ),
        };
      }),
    };

    this._filingService.saveFiling(filing).subscribe({
      next: (response: boolean) => {},
      error: (err) => {
        console.error("Error saving filing:", err);
      },
      complete: () => {
        this.isValidationModeActive = false;
        this.ref.close({ refresh: true });
      },
    });
  }

  validatePlan(trading: any) {
    this.calculateInitialExpirationDates(trading);
    const alreadyValidated = this.validatedPlans.find(
      (plan) => plan.tradingPlanId === trading.tradingPlanId
    );
    this.isValidationModeActive = true;
    if (!alreadyValidated) {
      this.validatedPlans.push(trading);

      this.disabledPlans.push(trading.tradingPlanId);
    }
  }

  onCheckboxChange(tradingPlanId: number, isChecked: boolean): void {
    if (isChecked) {
      if (!this.tradingPlansIdsForDeletion.includes(tradingPlanId)) {
        this.tradingPlansIdsForDeletion.push(tradingPlanId);
      }
    } else {
      this.tradingPlansIdsForDeletion = this.tradingPlansIdsForDeletion.filter(
        (id) => id !== tradingPlanId
      );
    }
  }
  // onDeleteSelectedPlans(): void {
  //   if (this.tradingPlansIdsForDeletion.length === 0) return;

  //   this._confirmationService.confirm({
  //     key: "trading-confirm",
  //     message: "Are you sure you want to delete the selected trading plans?",
  //     header: "Confirm Deletion",
  //     icon: "pi pi-exclamation-triangle",
  //     accept: () => {
  //       // Separate selected dynamic and real IDs
  //       const selectedDynamicIds = this.tradingPlansIdsForDeletion.filter(
  //         (id) => this.isDynamicId(id)
  //       );

  //       const selectedRealIds = this.tradingPlansIdsForDeletion.filter(
  //         (id) => !this.isDynamicId(id)
  //       );

  //       //  Remove only selected dynamic plans locally (no API call)
  //       if (selectedDynamicIds.length > 0) {
  //         this.filing.tradingPlans = this.filing.tradingPlans?.filter(
  //           (plan) => !selectedDynamicIds.includes(plan.tradingPlanId as any)
  //         );

  //         this._messageService.add({
  //           severity: "success",
  //           summary: "Removed",
  //           detail: "Selected temporary (dynamic) plans removed locally.",
  //         });
  //       }

  //       //  Send delete API only for real plans
  //       if (selectedRealIds.length > 0) {
  //         const payload = {
  //           tradingPlansIds: selectedRealIds,
  //           reason: "Incorrect plan created by LLM",
  //         };

  //         this._tradingPlanService.deleteTradingPlans(payload).subscribe({
  //           next: () => {
  //             this.filing.tradingPlans = this.filing.tradingPlans?.filter(
  //               (plan) => !selectedRealIds.includes(plan.tradingPlanId as any)
  //             );

  //             this.deletedPlansCount += selectedRealIds.length;
  //             this._messageService.add({
  //               severity: "success",
  //               summary: "Success",
  //               detail: "Selected trading plans deleted successfully from API.",
  //             });
  //           },
  //           error: () => {
  //             this._messageService.add({
  //               severity: "error",
  //               summary: "Error",
  //               detail: "Failed to delete trading plans from API.",
  //             });
  //           },
  //           complete: () => {
  //             this.tradingPlansIdsForDeletion = [];
  //           },
  //         });
  //       } else {
  //         // agar sirf dynamic delete hue to bhi array reset karo
  //         this.tradingPlansIdsForDeletion = [];
  //       }
  //     },
  //   });
  // }
  onDeleteSelectedPlans(): void {
    if (this.tradingPlansIdsForDeletion.length === 0) return;
    const selectedRealIds = this.tradingPlansIdsForDeletion.filter(
      (id) => !this.isDynamicId(id)
    );
    this._confirmationService.confirm({
      key: "trading-confirm",
      message: "Are you sure you want to delete the selected trading plans?",
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        // Filter out selected plans (dynamic or real dono) from list
        this.filing.tradingPlans = this.filing.tradingPlans?.filter(
          (plan) =>
            !this.tradingPlansIdsForDeletion.includes(plan.tradingPlanId as any)
        );

        // Show success message
        this._messageService.add({
          severity: "success",
          summary: "Hidden",
          detail: "Selected plans have been hidden deleted.",
        });

        // Reset selected IDs
        this.tradingPlansIdsForDeletion = [];
        this.realIdsToDelete = selectedRealIds;
      },
    });
  }
  onValidateClick(): void {
    if (this.realIdsToDelete.length > 0) {
      const payload = {
        tradingPlansIds: this.realIdsToDelete,
        reason: "Incorrect plan created by LLM",
      };

      this._tradingPlanService.deleteTradingPlans(payload).subscribe({
        next: () => {
          this.realIdsToDelete = [];

          // this._messageService.add({
          //   severity: "success",
          //   summary: "Deleted",
          //   detail: "Real plans deleted successfully",
          // });

          //  Now call validate after delete success
          this.validateForm();
        },
        error: () => {
          this._messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to delete real plans",
          });
        },
      });
    } else {
      this.validateForm();
    }
  }

  discardFiling(filing: any): void {
    this._confirmationService.confirm({
      key: "discardFiling",
      message: `Are you sure you want to discard filing #${filing.filingId}?`,
      header: "Confirm Discard",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this._filingService
          .discardFiling({
            filingId: filing.filingId,
            reason: "no plan found",
          })
          .subscribe({
            next: () => {
              this._messageService.add({
                severity: "success",
                summary: "Discarded",
                detail: `Filing #${filing.filingId} discarded successfully.`,
              });
            },
            error: (err) => {
              this._messageService.add({
                severity: "error",
                summary: "Error",
                detail: err?.error?.message || "Failed to discard filing.",
              });
            },
            complete: () => {
              this.ref.close({ refresh: true });
            },
          });
      },
      reject: () => {
        this._messageService.add({
          severity: "info",
          summary: "Cancelled",
          detail: "Discard action was cancelled.",
        });
      },
    });
  }

  formatToDateString(date: any): string | null {
    if (!date) return null;
    const d = new Date(date);
    return formatDate(d, "yyyy-MM-dd", "en-US");
  }
  onSelectionChange(security: AsReportedSecurityBreakdown, value: any) {
    (security as any).selectedTransactionType = value;
    // Agar BUY select hai to sellToCover hata do
    if (value === "BUY" && security.securityBreakdownTags?.sellToCover) {
      security.securityBreakdownTags = {
        ...security.securityBreakdownTags,
        sellToCover: false,
      };
    }
  }
  AddTags(security: AsReportedSecurityBreakdown): void {
    const ref = this.dialogService.open(AddSecurityTagComponent, {
      width: "30vw",
      data: {
        existingTags: security.securityBreakdownTags ?? {},
        transactionType:
          (security as any).selectedTransactionType ?? security.transactionType,
      },

      modal: true,
      closable: true,
      header: "Add Tags",
    });

    ref.onClose.subscribe((selectedTags: AsReportedSecurityBreakdownTags) => {
      if (selectedTags) {
        security.securityBreakdownTags = selectedTags;
      }
    });
  }

  removeSecurityBreakdown(security: AsReportedSecurityBreakdown): void {
    if (!security.asReportedSecurityBreakdownId) {
      // If no ID exists, just remove from the local array
      const plan = this.filing.tradingPlans?.find((p) =>
        p.asReportedSecurityBreakdowns?.some((b) => b === security)
      );
      if (plan) {
        plan.asReportedSecurityBreakdowns =
          plan.asReportedSecurityBreakdowns?.filter((b) => b !== security);
      }
      return;
    }

    this._confirmationService.confirm({
      message: `<div class="font-semibold text-base">
          Are you sure you want to delete this <strong>security breakdown</strong>?<br/>
          This action <u>cannot be undone</u>.
        </div>`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      rejectButtonStyleClass: "p-button-secondary p-button-sm",
      acceptButtonStyleClass: "p-button-danger p-button-sm",
      closable: true,
      rejectVisible: true,
      accept: () => {
        this._asReportedSecurityBreakdownService
          .deleteAsReportedSecurityBreakdown({
            asReportedSecurityBreakdownId:
              security.asReportedSecurityBreakdownId,
            reason: "Incorrect breakdown created by LLM",
          })
          .subscribe({
            next: (response: boolean) => {
              if (response) {
                // Remove from the local array
                const plan = this.filing.tradingPlans?.find((p) =>
                  p.asReportedSecurityBreakdowns?.some((b) => b === security)
                );
                if (plan) {
                  plan.asReportedSecurityBreakdowns =
                    plan.asReportedSecurityBreakdowns?.filter(
                      (b) => b !== security
                    );
                }

                this._messageService.add({
                  severity: "success",
                  summary: "Deleted",
                  detail: "Breakdown deleted successfully",
                });
              } else {
                this._messageService.add({
                  severity: "error",
                  summary: "Failed",
                  detail: "Breakdown could not be deleted",
                });
              }
            },
            error: (err: any) => {
              this._messageService.add({
                severity: "error",
                summary: "Error",
                detail: "An error occurred while deleting",
              });
              console.error(err);
            },
          });
      },
    });
  }
  private canCloseDialog(): boolean {
    if (!this.isValidationModeActive) return true;

    this._messageService.add({
      severity: "warn",
      summary: "Validation Required",
      detail: "Please validate all remaining plans before leaving the file.",
    });

    return false;
  }
  interceptDialogClose(data?: any) {
    if (!this.canCloseDialog()) {
      return;
    }
    DynamicDialogRef.prototype.close.apply(this.ref, [data]);
  }

  writeText(trading: any): void {
    const ref = this.dialogService.open(AddTextComponent, {
      width: "30vw",
      modal: true,
      closable: true,
      header: "Add Tag",
      data: {
        modificationComment: trading.modificationComment,
        modifiedFields: trading.modifiedFields,
        isEstimatedAdoptionDate: trading.isEstimatedAdoptionDate,
        isEstimatedTerminationDate: trading.isEstimatedTerminationDate || "",
        isEstimatedAmendedDate: trading.isEstimatedAmendedDate || "",
        tradingPlanId: trading.tradingPlanId,
      },
    });

    ref.onClose.subscribe((payload: string) => {
      if (!payload) return;

      try {
        const parsed = JSON.parse(payload);
        trading.modifiedFields = parsed?.modifiedFields ?? null;
        trading.modificationComment = parsed?.comment;
        trading.isEstimatedAdoptionDate =
          parsed?.is_estimated_adoption_date ?? null;
        trading.isEstimatedTerminationDate =
          parsed?.is_estimated_termination_date ?? null;
        trading.isEstimatedAmendedDate =
          parsed?.is_estimated_amended_date ?? null;
      } catch {
        trading.modifiedFields = null;
        trading.isEstimatedAdoptionDate = null;
        trading.isEstimatedTerminationDate = null;
        trading.isEstimatedAmendedDate = null;
        trading.modificationComment = null;
      }
    });
  }
  calculateInitialExpirationDates(plan: any): void {
    // Skip if expiration date already exists
    if (plan.expirationDate) return;

    // Only calculate if planAction is ADOPTION
    if (plan.planAction !== "ADOPTION") return;

    const adoptionDate = plan.adoptionDate ? new Date(plan.adoptionDate) : null;
    const duration = plan.durationDays ? Number(plan.durationDays) : NaN;

    // Only calculate if both adoptionDate and durationDays are valid
    if (adoptionDate && !isNaN(duration)) {
      const expiration = new Date(adoptionDate);
      expiration.setDate(adoptionDate.getDate() + duration);
      plan.expirationDate = this.formatToDateString(expiration) || undefined;
    }
  }
  isDynamicId(id: any): boolean {
    // Example: if dynamic IDs are strings like 'temp-' or not numeric
    return typeof id === "string" && id.startsWith("plan-");
  }
  getStringForTransactionTags(
    securityBreakDownTags: AsReportedSecurityBreakdownTags | null
  ): string {
    if (!securityBreakDownTags) {
      return "";
    }
    return (
      [
        ["awardRelated", "A"],
        ["optionsRelated", "O"],
        ["giftRelated", "G"],
        ["taxWithheld", "T"],
        ["remuneration", "R"],
        ["isIndeterminable", "I"],
        ["sellToCover", "S"],
      ] as [keyof AsReportedSecurityBreakdownTags, string][]
    )
      .filter(([key]) => securityBreakDownTags[key])
      .map(([_, letter]) => letter)
      .join(" ");
  }
}
